/**
 * The component gallery's catalog (Fase D, 2026-10-24): ONE list of entries
 * from the four sources the platform draws with, grouped into the 18
 * categories approved in the review bench.
 *
 *  - the kit (@omnitok/ui v3): `KIT_ENTRIES`, live demos in this folder;
 *  - bi-components: `SHOWCASE_ENTRIES`, the manifest (the source of truth
 *    the parity script and the Storybook in tools/storybook also read);
 *  - dashboard-render: `DASHBOARD_RENDER_ENTRIES`;
 *  - compositions that are not one export: `EXTRA_ENTRIES`.
 *
 * The manifests keep their own categories; the mapping below is the
 * gallery's reading of them (a chart and its interactive twin are ONE card,
 * the analytics family splits into measures and compositions).
 */
import type { ReactNode } from "react";
import { isValidElement } from "react";

import { SHOWCASE_ENTRIES } from "@pervasivemind/bi-components/showcase-manifest";
import { DASHBOARD_RENDER_ENTRIES } from "@pervasivemind/dashboard-render";

import { EXTRA_ENTRIES } from "./extra-demos";
import { KIT_ENTRIES } from "./kit-demos";

export type GallerySource = "ui" | "bi" | "render" | "admin";

export interface GalleryEntry {
  id: string;
  category: string;
  title: string;
  subtitle?: string;
  componentName: string;
  /** Every export the card documents (a chart + its interactive twin). */
  covers?: string[];
  /** Parts of a composition, shown as "Made of …". */
  composition?: string[];
  source: GallerySource;
  fullHeight?: number;
  render: (h: number, w: number, locale?: string) => ReactNode;
}

/** The 18 categories, in the order of the list, with the group that opens. */
export const CATEGORIES: ReadonlyArray<{ id: string; group?: "system" | "data" | "composition" }> = [
  { id: "foundation", group: "system" },
  { id: "navigation" },
  { id: "controls" },
  { id: "surfaces" },
  { id: "feedback" },
  { id: "layout" },
  { id: "drag" },
  { id: "kpis", group: "data" },
  { id: "charts" },
  { id: "tables" },
  { id: "filters" },
  { id: "dates" },
  { id: "measures" },
  { id: "maps" },
  { id: "compositions", group: "composition" },
  { id: "motion-page" },
  { id: "transitions" },
  { id: "brand" },
];
export const CATEGORY_IDS = new Set(CATEGORIES.map((c) => c.id));

/** Categories whose cards read better one per row. */
export const ONE_COLUMN = new Set([
  "navigation", "filters", "tables", "dates", "transitions", "motion-page",
  "compositions", "brand", "layout", "drag", "feedback",
]);

const PAIRED = ["VerticalBar", "HorizontalBar", "LineChart", "MultiLine", "GroupedBar", "ComboChart", "PieChart", "ScatterChart", "BubbleChart"];
const COMPOSITION_PARTS: Record<string, string[]> = {
  ScoreCard: ["ChartCard", "ProgressRing", "MeterBar"],
  AnomalyCard: ["Callout", "DeltaChip"],
  MetricStrip: ["MeterBar", "StatusChip"],
  ResponsiveCardGrid: ["ChartCard"],
};
/** Covered by another card: the kit's collapsible header demo, every chart
 *  card (each wraps its chart in ChartDataExplorer) and the interactive
 *  twins (merged into their static chart). */
const COVERED = new Set(["CollapsibleFilterHeader", "ChartDataExplorer", ...PAIRED.map((n) => `${n}T`)]);

function categoryOf(e: { category: string; componentName: string }): string {
  if (e.componentName === "Callout") return "feedback";
  if (e.componentName === "SavedFilters") return "filters";
  if (e.componentName === "TimelineChart") return "charts";
  if (e.category.startsWith("charts-")) return "charts";
  if (e.category === "analytics") return COMPOSITION_PARTS[e.componentName] ? "compositions" : "measures";
  if (e.category === "report") return "compositions";
  return e.category;
}

type ManifestEntry = (typeof SHOWCASE_ENTRIES)[number];

function fromManifest(e: ManifestEntry, source: GallerySource, all: ReadonlyArray<ManifestEntry>): GalleryEntry {
  const next: GalleryEntry = {
    id: e.id,
    category: categoryOf(e),
    title: e.title,
    subtitle: e.subtitle,
    componentName: e.componentName,
    source,
    render: e.render as GalleryEntry["render"],
  };
  if (COMPOSITION_PARTS[e.componentName]) next.composition = COMPOSITION_PARTS[e.componentName];
  if (PAIRED.includes(e.componentName)) {
    const twin = all.find((t) => t.componentName === `${e.componentName}T`);
    if (twin) {
      next.render = twin.render as GalleryEntry["render"];
      next.covers = [e.componentName, `${e.componentName}T`];
    }
  }
  return next;
}

function build(): GalleryEntry[] {
  const manifest = [...SHOWCASE_ENTRIES, ...DASHBOARD_RENDER_ENTRIES] as ManifestEntry[];
  const bi = (SHOWCASE_ENTRIES as ManifestEntry[])
    .filter((e) => !COVERED.has(e.componentName))
    .map((e) => fromManifest(e, "bi", manifest));
  const render = (DASHBOARD_RENDER_ENTRIES as ManifestEntry[])
    .filter((e) => !COVERED.has(e.componentName))
    .map((e) => fromManifest(e, "render", manifest));
  const entries = [...(KIT_ENTRIES as GalleryEntry[]), ...bi, ...render, ...(EXTRA_ENTRIES as GalleryEntry[])];
  // Fail loud: an entry in a category the list does not have would never
  // be reachable, and a duplicate id would make two cards share a URL.
  const unknown = entries.filter((e) => !CATEGORY_IDS.has(e.category));
  if (unknown.length) {
    throw new Error(`Gallery: entries in unknown categories: ${unknown.map((e) => `${e.id} (${e.category})`).join(", ")}`);
  }
  const ids = new Set<string>();
  for (const e of entries) {
    if (ids.has(e.id)) throw new Error(`Gallery: duplicate entry id ${e.id}`);
    ids.add(e.id);
  }
  return entries;
}

export const GALLERY_ENTRIES: ReadonlyArray<GalleryEntry> = build();

/** The rows a chart demo plots, for its optional data table. */
export function chartRows(node: ReactNode): Array<Record<string, string | number | null>> {
  if (!isValidElement(node)) return [];
  const data = (node.props as { data?: unknown }).data;
  if (!Array.isArray(data)) return [];
  return data.flatMap((row, i) =>
    typeof row === "number"
      ? [{ "#": i + 1, value: row }]
      : row && typeof row === "object"
        ? [Object.fromEntries(Object.entries(row as Record<string, unknown>).filter(([, v]) => v == null || ["string", "number"].includes(typeof v))) as Record<string, string | number | null>]
        : [],
  );
}

/** The import line a card's copy button writes. */
export function importLine(entry: GalleryEntry): string {
  const pkg = {
    ui: "@omnitok/ui",
    bi: "@pervasivemind/bi-components",
    render: "@pervasivemind/dashboard-render",
    admin: "@pervasivemind/admin-ui",
  }[entry.source];
  const names = entry.covers ?? [entry.componentName];
  return `import { ${names.join(", ")} } from "${pkg}";`;
}
